import { useEffect, useMemo, useState } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";

const numberValue = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const unwrapData = (response) => {
  const data = response?.data?.data ?? response?.data ?? response ?? [];
  return Array.isArray(data) ? data : [];
};

const getRemainingWeight = (item, totalField) => {
  if (item?.unlimitedWeight) return 0;
  return Math.max(
    0,
    numberValue(item?.[totalField]) - numberValue(item?.actualWeight),
  );
};

const formatNumber = (value) =>
  numberValue(value).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function SalesQuotaReport() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [orderBy, setOrderBy] = useState("orderRemaining");
  const [order, setOrder] = useState("desc");

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const { shopId } = services.getSalengContext();
      if (!shopId) throw new Error("ไม่พบข้อมูลร้านค้า");

      const [inventoryResponse, purchaseOrders, saleOrdersResponse] =
        await Promise.all([
          services.getInventory(),
          services.getPurchaseOrders({ status: "open" }),
          services.getSaleOrders({ shopId }),
        ]);

      const inventory = unwrapData(inventoryResponse);
      const purchaseOrdersData = Array.isArray(purchaseOrders)
        ? purchaseOrders
        : unwrapData(purchaseOrders);
      const saleOrders = unwrapData(saleOrdersResponse).filter(
        (saleOrder) =>
          String(saleOrder?.status || "").toLowerCase() !== "close" &&
          (saleOrder?.customer?.shopId == null ||
            String(saleOrder.customer.shopId) === String(shopId)),
      );

      const productMap = new Map();
      const addProduct = (productLike) => {
        const product = productLike?.product ?? productLike;
        const productId = productLike?.productId ?? product?.id;
        if (productId == null) return;
        const key = String(productId);
        if (!productMap.has(key)) {
          productMap.set(key, {
            productId: Number(productId),
            categoryMain: product?.category?.name ?? productLike?.category?.name ?? "-",
            categorySub: product?.subCategory?.name ?? productLike?.subCategory?.name ?? "-",
            productCode: product?.code ?? productLike?.code ?? "-",
            productTitle: product?.title ?? productLike?.title ?? productLike?.productName ?? "-",
            unit: product?.unit ?? productLike?.unit ?? "กก.",
          });
        }
      };

      inventory.forEach(addProduct);
      purchaseOrdersData.forEach((purchaseOrder) =>
        (purchaseOrder?.items || []).forEach(addProduct),
      );
      saleOrders.forEach((saleOrder) =>
        (saleOrder?.items || []).forEach(addProduct),
      );

      const inventoryByProduct = new Map();
      inventory.forEach((item) => {
        const productId = item?.productId ?? item?.product?.id;
        if (productId == null) return;
        inventoryByProduct.set(
          String(productId),
          numberValue(item?.quantity),
        );
      });

      const purchaseOrderByProduct = new Map();
      purchaseOrdersData
        .filter(
          (purchaseOrder) =>
            String(purchaseOrder?.status || "").toLowerCase() !== "close" &&
            purchaseOrder?.deletedAt == null,
        )
        .forEach((purchaseOrder) => {
          (purchaseOrder?.items || []).forEach((item) => {
            if (item?.deletedAt != null) return;
            const productId = item?.productId ?? item?.product?.id;
            if (productId == null) return;
            const remaining = getRemainingWeight(item, "poWeight");
            purchaseOrderByProduct.set(
              String(productId),
              (purchaseOrderByProduct.get(String(productId)) || 0) + remaining,
            );
          });
        });

      const saleOrderByProduct = new Map();
      saleOrders.forEach((saleOrder) => {
        (saleOrder?.items || []).forEach((item) => {
          const productId = item?.productId ?? item?.product?.id;
          if (productId == null) return;
          const remaining = getRemainingWeight(item, "weight");
          saleOrderByProduct.set(
            String(productId),
            (saleOrderByProduct.get(String(productId)) || 0) + remaining,
          );
        });
      });

      setRows(
        Array.from(productMap.values()).map((product) => {
          const key = String(product.productId);
          const inventoryWeight = inventoryByProduct.get(key) || 0;
          const poRemainingWeight = purchaseOrderByProduct.get(key) || 0;
          const predictedWeight = inventoryWeight + poRemainingWeight;
          const saleOrderWeight = saleOrderByProduct.get(key) || 0;

          return {
            ...product,
            inventoryWeight,
            poRemainingWeight,
            predictedWeight,
            saleOrderWeight,
            orderRemaining: saleOrderWeight - predictedWeight,
          };
        }),
      );
    } catch (loadError) {
      console.error("โหลดรายงานโควต้าการซื้อขายไม่สำเร็จ", loadError);
      setRows([]);
      setError("ไม่สามารถโหลดข้อมูลรายงานได้");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      [row.categoryMain, row.categorySub, row.productCode, row.productTitle]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const result = [...filteredRows];
    result.sort((left, right) => {
      const leftValue = left[orderBy];
      const rightValue = right[orderBy];
      const comparison =
        typeof leftValue === "string"
          ? String(leftValue).localeCompare(String(rightValue), "th")
          : numberValue(leftValue) - numberValue(rightValue);
      return order === "asc" ? comparison : -comparison;
    });
    return result;
  }, [filteredRows, order, orderBy]);

  const totals = useMemo(
    () =>
      sortedRows.reduce(
        (summary, row) => {
          summary.inventoryWeight += numberValue(row.inventoryWeight);
          summary.poRemainingWeight += numberValue(row.poRemainingWeight);
          summary.predictedWeight += numberValue(row.predictedWeight);
          summary.saleOrderWeight += numberValue(row.saleOrderWeight);
          summary.orderRemaining += numberValue(row.orderRemaining);
          return summary;
        },
        {
          inventoryWeight: 0,
          poRemainingWeight: 0,
          predictedWeight: 0,
          saleOrderWeight: 0,
          orderRemaining: 0,
        },
      ),
    [sortedRows],
  );

  const requestSort = (field) => {
    if (orderBy === field) {
      setOrder((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setOrderBy(field);
    setOrder(["categoryMain", "categorySub", "productCode", "productTitle"].includes(field) ? "asc" : "desc");
  };

  const exportRows = () => {
    if (sortedRows.length === 0) return;
    const exportData = sortedRows.map((row) => ({
      หมวดหมู่หลัก: row.categoryMain,
      หมวดหมู่ย่อย: row.categorySub,
      รหัส: row.productCode,
      สินค้า: row.productTitle,
      หน่วย: row.unit,
      "น้ำหนักสินค้าในคลัง": row.inventoryWeight.toFixed(2),
      "น้ำหนักคงเหลือจาก PO": row.poRemainingWeight.toFixed(2),
      "น้ำหนักรวมคาดการณ์": row.predictedWeight.toFixed(2),
      ออเดอร์ขาย: row.saleOrderWeight.toFixed(2),
      ออเดอร์คงเหลือ: row.orderRemaining.toFixed(2),
    }));
    const headers = Object.keys(exportData[0]);
    const csv = [
      headers.join(","),
      ...exportData.map((row) =>
        headers.map((header) => `"${row[header] ?? ""}"`).join(","),
      ),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "sales_quota_report.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const columns = [
    ["categoryMain", "หมวดหมู่หลัก"],
    ["categorySub", "หมวดหมู่ย่อย"],
    ["productCode", "รหัส"],
    ["productTitle", "สินค้า"],
    ["unit", "หน่วย"],
    ["inventoryWeight", "น้ำหนักสินค้าในคลัง"],
    ["poRemainingWeight", "น้ำหนักคงเหลือ (จาก PO)"],
    ["predictedWeight", "น้ำหนักรวม (คาดการณ์)"],
    ["saleOrderWeight", "ออเดอร์ขาย"],
    ["orderRemaining", "ออเดอร์คงเหลือ"],
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-[1800px] mx-auto">
        <AppBreadcrumbs />
        <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h1 className="text-2xl font-bold text-gray-800">
              สรุปโควต้าการซื้อขายสินค้า
            </h1>
            <div className="flex flex-wrap gap-3">
              <div className="relative w-full md:w-80">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="ค้นหารหัสหรือชื่อสินค้า"
                  className="w-full pl-10 pr-3 py-2 rounded-xl border border-gray-200 bg-gray-50 outline-none focus:border-blue-500"
                />
              </div>
              <button
                type="button"
                onClick={exportRows}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700"
              >
                <ContentCopyIcon fontSize="small" />
                Export
              </button>
              <button
                type="button"
                onClick={loadData}
                className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                รีเฟรช
              </button>
            </div>
          </div>
        </div>

        <div className="mt-5 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-500">
              กำลังโหลดข้อมูล...
            </div>
          ) : error ? (
            <div className="h-64 flex items-center justify-center text-red-600">
              {error}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1500px] text-sm">
                <thead className="bg-blue-50 text-blue-700 border-b border-blue-100">
                  <tr>
                    {columns.map(([field, label]) => (
                      <th
                        key={field}
                        onClick={() => requestSort(field)}
                        className={`px-4 py-4 font-semibold whitespace-nowrap cursor-pointer hover:bg-blue-100 ${
                          field === "unit" || field.includes("Weight") || field === "orderRemaining"
                            ? "text-right"
                            : "text-left"
                        }`}
                      >
                        {label} {orderBy === field ? (order === "asc" ? "↑" : "↓") : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedRows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                        ไม่พบข้อมูล
                      </td>
                    </tr>
                  ) : (
                    sortedRows.map((row) => (
                      <tr key={row.productId} className="hover:bg-blue-50/30">
                        <td className="px-4 py-3 text-gray-600">{row.categoryMain}</td>
                        <td className="px-4 py-3 text-gray-600">{row.categorySub}</td>
                        <td className="px-4 py-3 text-gray-600">{row.productCode}</td>
                        <td className="px-4 py-3 font-medium text-gray-800">{row.productTitle}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{row.unit}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{formatNumber(row.inventoryWeight)}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{formatNumber(row.poRemainingWeight)}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800">{formatNumber(row.predictedWeight)}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{formatNumber(row.saleOrderWeight)}</td>
                        <td className={`px-4 py-3 text-right font-bold ${row.orderRemaining < 0 ? "text-red-600" : "text-black"}`}>
                          {formatNumber(row.orderRemaining)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr className="font-bold text-gray-800">
                    <td colSpan={5} className="px-4 py-4 text-right">
                      รวม
                    </td>
                    <td className="px-4 py-4 text-right">{formatNumber(totals.inventoryWeight)}</td>
                    <td className="px-4 py-4 text-right">{formatNumber(totals.poRemainingWeight)}</td>
                    <td className="px-4 py-4 text-right">{formatNumber(totals.predictedWeight)}</td>
                    <td className="px-4 py-4 text-right">{formatNumber(totals.saleOrderWeight)}</td>
                    <td className={`px-4 py-4 text-right ${totals.orderRemaining < 0 ? "text-red-600" : "text-black"}`}>
                      {formatNumber(totals.orderRemaining)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
